import { Inject } from '@odinjs/odin';
import { ActionAPI, BaseModalProps, ButtonAPI, DeckAPI, ModalButton, ModalService, PanelAPI } from '@philips-internal/oswald-ui-components';
import { ModalButtonType, ModalType, ToastLevel } from '@philips-internal/oswald-ui-constants';
import Controller, { OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { FragmentNames, NeonatalTpnViewerAliases } from '../../../../../codegen';
import { ActionType, InfusionHourStatus, InfusionItemStatus } from '../../../../../constants/infusion-solutions';
import { Endpoint } from '../../../../../constants/endpoints';
import ShowToast from '../../../../../utils/toast-service';
import BaseController from '../../../../BaseController';
import InformationBarUtils from '../../../../commons/header/information-bar-header-utils';
import { MedicationAndDateHeaderParams } from '../../../../commons/header/medication-and-date-header-params';
import TimelineContext, { PrescriptionType, TimeLineEventMedication } from '../../../services/timeline-service';
import { MedicationViewerParams } from '../../medication-viewer/medication-viewer-params';
import { NeonatalTpnType } from '../neonatal-tpn-types';
import { AdminByScheduleParams } from '../../administration-schedule/administration-schedule-params';
import { AbortableCloseEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { AdminByProcessParams } from '../../medication-administration-by-process/medication-administration-by-process-params';
import { FoundationUtils } from '../../../../../utils/foundation-utils';
import { ADEP_E1113Parameters, FunctionName } from '../../../../../utils/parameters';
import { YesNoType } from '../../../../../utils/enum-utils';
import { ApplicationRoute } from '../../../../../constants/routes';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { InfusionsViewerParams } from '../../../../../controllers/adep/infusion-viewer/infusions-viewer-params';
import { MedicineAndSolutionsParams } from '../../medicine-and-solutions/medicine-and-solutions-params';

@Controller({ component: NeonatalTpnViewerAliases.NEONATAL_TPN_VIEWER_LAYOUT, fragment: FragmentNames.NEONATAL_TPN_VIEWER })
export default class NeonatalTpnViewerController extends BaseController {

  @Inject(NeonatalTpnViewerAliases.NEONATAL_TPN_CONTENT_PANEL)
  private contentPanelApi!: PanelAPI;

  @Inject(NeonatalTpnViewerAliases.NEONATAL_TPN_FOOTER_PANEL)
  private footerPanelApi!: PanelAPI;

  @Inject(NeonatalTpnViewerAliases.START_BUTTON)
  private startButtonApi!: ButtonAPI;

  @Inject(NeonatalTpnViewerAliases.INTERRUPT_BUTTON)
  private interruptButtonApi!: ButtonAPI;

  @Inject(NeonatalTpnViewerAliases.PANEL_HEADER)
  private panelHeaderApi!: PanelAPI

  @OnMount(NeonatalTpnViewerAliases.NEONATAL_TPN_VIEWER_LAYOUT)
  handleMountLayout(): void {
    InformationBarUtils.collapsedBar = true;
    const { patientId, encounterId, itemType } = this.historyService.getCurrentRouteParams();
    const timelineMedication = TimelineContext.get() as TimeLineEventMedication;

    this.panelHeaderApi.setVisible(true)
    this.fetchNeonatalTpn().then((response) => {
      const neonatalTpn = response as NeonatalTpnType;

      MedicationViewerParams.adminSchedule = {
        dilutionDescription: '',
        materialSequence: timelineMedication.id,
        medicationHour: timelineMedication.start,
        prescriptionNumber: timelineMedication.prescriptionNumber,
      };

      MedicationAndDateHeaderParams.medicationAndDateHeaderProps = {
        medicationName: timelineMedication.description,
        scheduleDate: timelineMedication.start,
        type: 'NEONATAL_PARENTERAL_NUTRITION'
      };

      this.contentPanelApi.setVisible(true)
      this.footerPanelApi.setVisible(true)
      this.context.set({
        encounterId,
        neonatalTpn: {
          ...neonatalTpn,
          itemStatus: timelineMedication.itemStatus,
          itemType: itemType,
          materialDescription: timelineMedication.description,
          materialSequence: timelineMedication.itemCode,
          scheduleDate: timelineMedication.start,
          scheduleSequence: timelineMedication.id,
          status: timelineMedication.status
        },
        patientId,
      });
    }).catch((error) => {
      if (error.body) {
        ShowToast.open(error.body.errorMessage, ToastLevel.ERROR)
      }
    })
  }

  @OnMount(NeonatalTpnViewerAliases.NEONATAL_TPN_DECK)
  handleMountDeck({ target }: EventData<DeckAPI>): void {
    const timelineMedication = TimelineContext.get() as TimeLineEventMedication;
    const neonatalTpn = this.context.get('neonatalTpn') as NeonatalTpnType

    target.setData([{
      description: timelineMedication.description,
      doseQuantity: timelineMedication.itemQuantity,
      infusionSpeed: neonatalTpn.dripQuantity,
      isChemotherapy: false,
      prescriptionId: timelineMedication.prescriptionNumber,
      prescriptionNumber: timelineMedication.prescriptionNumber,
      routeAdministrationDescription: neonatalTpn.administrationRoute,
      scheduleDate: timelineMedication.start,
      stageQuantity: neonatalTpn.stageQuantity,
      statusItem: timelineMedication.status,
      totalVolume: neonatalTpn.totalVolume
    }]);
  }

  @OnMount(NeonatalTpnViewerAliases.START_BUTTON)
  handleMountStartButton(): void {
    this.validateAndSetButtonVisibility();
  }

  @OnMount(NeonatalTpnViewerAliases.INTERRUPT_BUTTON)
  handleMountInterruptButton(): void {
    const { itemStatus } = this.historyService.getCurrentRouteParams() as { itemStatus: string };
    const isValidItemStatusForInterrupt = [InfusionItemStatus.STARTED, InfusionItemStatus.RESTARTED].some(status => status === itemStatus);
    this.interruptButtonApi.setVisible(isValidItemStatusForInterrupt);
  }

  @OnMount(NeonatalTpnViewerAliases.CONTEXTUAL_BAR_ACTION_TERMINATE)
  handleMountActionterminate(event: MountEvent): void {
    const actionApi = event.target as ActionAPI
    actionApi.setEnabled(this.shouldShowTerminateAction());
  }

  @OnClick(NeonatalTpnViewerAliases.CONTEXTUAL_BAR_ACTION_TERMINATE)
  handleClickTerminateAction(): void {
    const { totalVolume } = this.context.getBulkData() as unknown as NeonatalTpnType;
    const { patientId, encounterId, prescriptionNumber, scheduleSequenceNumber, sequenceNumber, startDate, currentSpeedInfusion } = this.historyService.getCurrentRouteParams();
    applicationContext.redirect(ApplicationRoute.ADEP_NEONATAL_TPN_TERMINATE, { currentSpeedInfusion, encounterId, patientId, prescriptionNumber, scheduleSequenceNumber, sequenceNumber, startDate, totalVolume });
  }

  @OnClick(NeonatalTpnViewerAliases.INTERRUPT_BUTTON)
  handleClickInterruptButton(): void {
    const interruptExpression = 'button.infusion-viewer.interrupt';
    const interruptDescription = MessageService.translate(interruptExpression);
    const neonatalTpn = this.context.getBulkData() as unknown as NeonatalTpnType;
    const dialogModal: BaseModalProps = {
      contextData: { neonatalTpn },
      focusOnOpen: false,
      fragment: FragmentNames.NEONATAL_TPN_INTERRUPT,
      id: FragmentNames.NEONATAL_TPN_INTERRUPT,
      onBeforeClose: (event) => this.dialogModalConfirm(event, FragmentNames.NEONATAL_TPN_INTERRUPT),
      title: interruptDescription,
      type: ModalType.DEFAULT,
    };
    AdminByProcessParams.admType = { isAdmProcess: false };
    ModalService.openDefault(dialogModal);
  }

  @OnMount(NeonatalTpnViewerAliases.CONTEXTUAL_BAR_ACTION_ADJUST_SPEED)
  handleMountActionAdjustSpeed({ target }: EventData<ActionAPI>): void {
    const { itemStatus } = this.historyService.getCurrentRouteParams();
    const isValidStatus = [InfusionItemStatus.STARTED, InfusionItemStatus.RESTARTED].some(status => status === itemStatus);

    target.setEnabled(isValidStatus);
  }

  @OnClick(NeonatalTpnViewerAliases.CONTEXTUAL_BAR_ACTION_ADJUST_SPEED)
  handleClickAdjustSpeedAction(): void {
    const { patientId, encounterId, prescriptionNumber, itemType, sequenceNumber } = this.historyService.getCurrentRouteParams();
    const { neonatalTpn } = this.context.getBulkData() as unknown as {neonatalTpn: NeonatalTpnType};

    InfusionsViewerParams.adminSchedule = {
      dosageTypeDescription: neonatalTpn.unitMeasurementDescription
    };
    InfusionsViewerParams.infusionDetailsParams = {
      ...InfusionsViewerParams.infusionDetailsParams,
      isTypeDosage: neonatalTpn.unitMeasurement,
      itemType: this.getPrescriptionTypeByItemType(String(itemType)),
    }
    InfusionsViewerParams.params = {
      currentSpeedInfusion: neonatalTpn.dripQuantity ?? 0,
      establishmentCode: MedicineAndSolutionsParams.params.nrGroupingId,
      infusionName: MedicineAndSolutionsParams.params.dsMaterialId,
      prescriptionNumber: String(prescriptionNumber),
      solutionNumber: String(sequenceNumber),
    }

    applicationContext.redirect(ApplicationRoute.ADEP_ADJUST_INFUSION_SPEED, {
      encounterId,
      patientId,
    });
  }

  @OnClick(NeonatalTpnViewerAliases.START_BUTTON)
  handleClickExecuteTimeButton(): void {
    const { itemStatus } = this.historyService.getCurrentRouteParams() as { itemStatus: string };
    const startSolution = itemStatus === InfusionItemStatus.NOT_STARTED ? 'button.infusion-viewer.start' : 'button.infusion-viewer.restart-solution';
    const scheduleTitleDescription = MessageService.translate(startSolution);
    const neonatalTpn = this.context.getBulkData();
    const startNeonatalTpnFragment = FragmentNames.NEONATAL_TPN_START
    const dialogModal: BaseModalProps = {
      contextData: { neonatalTpn: neonatalTpn },
      focusOnOpen: false,
      fragment: startNeonatalTpnFragment,
      id: startNeonatalTpnFragment,
      onBeforeClose: (event) => this.dialogModalConfirm(event, startNeonatalTpnFragment),
      title: scheduleTitleDescription,
      type: ModalType.DEFAULT,
    };

    ModalService.openDefault(dialogModal);
  }

  @OnMount (NeonatalTpnViewerAliases.CONTEXTUAL_BAR_ACTION_REVERSE)
  handleMountActionReverse(event: MountEvent): void {
    const actionApi = event.target as ActionAPI
    actionApi.setEnabled(this.shouldShowTerminateAction());
  }

  @OnClick (NeonatalTpnViewerAliases.CONTEXTUAL_BAR_ACTION_REVERSE)
  handleClickActionReverse (): void {
    const { patientId, encounterId, prescriptionNumber, sequenceNumber } = this.historyService.getCurrentRouteParams();
    const itemType = PrescriptionType.NEONATAL_PARENTERAL_NUTRITION
    const solutionType = 7
    applicationContext.redirect(ApplicationRoute.ADEP_REVERSE_TPN, { encounterId, itemType, patientId, prescriptionNumber, sequenceNumber, solutionType });
  }

  private getPrescriptionTypeByItemType(itemType: string): PrescriptionType {
    switch (itemType) {
      case 'NPN':
        return PrescriptionType.NEONATAL_PARENTERAL_NUTRITION;
      default:
        return PrescriptionType.NEONATAL_PARENTERAL_PEDIATRIC;
    }
  }

  private validateAndSetButtonVisibility(): void {
    const { hourStatus, itemStatus } = this.historyService.getCurrentRouteParams() as { hourStatus: string, itemStatus: string };
    const isValidItemStatusForAdministered = [InfusionItemStatus.II, InfusionItemStatus.INTERRUPTED, InfusionItemStatus.RESTARTED, InfusionItemStatus.T].some(status => status === itemStatus);
    const isNotStarted = hourStatus === InfusionHourStatus.PENDING && itemStatus === InfusionItemStatus.RESTARTED

    if ((hourStatus === InfusionHourStatus.ADMINISTERED && isValidItemStatusForAdministered) || isNotStarted) {
      this.startButtonApi.setVisible(false);
      return;
    }

    if (this.shouldStartNeonatalTpn(itemStatus, hourStatus)) {
      const label = this.setScreenLabel(ActionType.START_SOLUTION, hourStatus);
      this.setExecuteTimeButton(label)
      return;
    }

    if (this.shouldRestartNeonatalTpn(itemStatus, hourStatus)) {
      const label = this.setScreenLabel(ActionType.RESTART_SOLUTION, hourStatus);
      this.setExecuteTimeButton(label)
      return;
    }

    this.startButtonApi.setVisible(false);
  }

  private shouldStartNeonatalTpn(itemStatus: string, status: string): boolean {
    const isItemStatusValid = itemStatus === InfusionItemStatus.NOT_STARTED;
    const isStatusValid = status === InfusionHourStatus.PENDING || status === InfusionHourStatus.PREPARATION;
    return isItemStatusValid && isStatusValid
  }

  private shouldRestartNeonatalTpn(itemStatus: string, status: string): boolean {
    const isInterrupted = this.isInterrupted(itemStatus) && status === InfusionHourStatus.INTERRUPTED;
    const isPending = itemStatus === InfusionItemStatus.INTERRUPTED && status === InfusionHourStatus.PENDING;
    return isInterrupted || isPending;
  }

  private setScreenLabel(actionType: string, status: string): string {
    const { itemStatus } = this.historyService.getCurrentRouteParams() as { itemStatus: string };
    const isInterrupted = this.isInterrupted(itemStatus)

    if (
      actionType !== ActionType.START_SOLUTION ||
      (status === InfusionHourStatus.INTERRUPTED && isInterrupted) ||
      (status === InfusionHourStatus.PENDING && itemStatus === InfusionItemStatus.INTERRUPTED)
    ) {
      return 'button.infusion-viewer.restart'
    }

    if (status !== InfusionHourStatus.STARTED) {
      return 'text.start-solution-by-schedule'
    }

    return ''
  }

  private isInterrupted(itemStatus: string): boolean {
    return [InfusionItemStatus.II.valueOf(), InfusionItemStatus.INTERRUPTED.valueOf()].includes(itemStatus)
  }

  private setExecuteTimeButton(label: string): void {
    this.startButtonApi.setValue(MessageService.translate(label));
    this.startButtonApi.setVisible(true);
  }

  private dialogModalConfirm(event: AbortableCloseEvent, id: string): void {
    const noCardsRead = AdminByScheduleParams.readParam?.noCardsRead;

    if (noCardsRead) {
      ModalService.close(id);
      ModalService.close('dialog-modal-service');
      return;
    }

    event.abort();

    const buttons: ModalButton[] = [];

    buttons.push({
      action: () => { },
      title: 'tws.biometry.cancel',
      type: ModalButtonType.CANCEL,
    });

    buttons.push({
      action: () => this.redirectToTimeline(id),
      title: 'tws.file-uploader-modal.confirm',
      type: ModalButtonType.SUCCESS,
    });

    const dialogModal: BaseModalProps = {
      dialogDescription: MessageService.translate('text.reset items-want-to-continue'),
      dockButtons: buttons,
      focusOnOpen: true,
      id: 'dialog-modal-service',
      title: 'text.message-alert-attention',
    };

    ModalService.openDialog(dialogModal);
  }

  private redirectToTimeline(id: string): void {
    ModalService.close(id);
    ModalService.close('dialog-modal-service');
  }

  private fetchNeonatalTpn(): Promise<NeonatalTpnType> {
    const { itemSequence } = TimelineContext.get() as TimeLineEventMedication;

    return HttpService.get<NeonatalTpnType>(Endpoint.EMAR_GET_NEONATAL_TPN_INFORMATION, {
      scheduleSequenceNumber: itemSequence,
    }).then((response) => {
      const responseBody = response.body as NeonatalTpnType;
      this.context.set({ ...responseBody });
      return responseBody;
    })
  }

  private shouldShowTerminateAction(): boolean {
    const param27 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_ONLY_CONSULTING_EMAR)
    if (param27 === YesNoType.YES) {
      return false
    }
    const { itemStatus } = this.historyService.getCurrentRouteParams();
    const isValidStatus = [InfusionItemStatus.STARTED, InfusionItemStatus.RESTARTED, InfusionItemStatus.INTERRUPTED, InfusionItemStatus.II].some(st => st === itemStatus);
    return isValidStatus
  }
}